import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { 
  Send, Zap, LogOut, ArrowLeft, RotateCcw, Check, CheckCheck, 
  Globe, User, FileText, MessageSquare, CheckCircle, Download,
  ChevronDown, X, Loader2, AlertCircle, WifiOff, Mic, Paperclip, Upload
} from 'lucide-react';

const LANGUAGES = [
  { code: 'en', name: 'English', native: 'English', flag: '🇬🇧' },
  { code: 'hi', name: 'Hindi', native: 'हिन्दी', flag: '🇮🇳' },
  { code: 'mr', name: 'Marathi', native: 'मराठी', flag: '🇮🇳' },
  { code: 'gu', name: 'Gujarati', native: 'ગુજરાતી', flag: '🇮🇳' },
];

const JOURNEY_STEPS = [
  { key: 'language_selection', label: 'Language', icon: Globe },
  { key: 'pre_intake', label: 'Identity', icon: User },
  { key: 'template_driven', label: 'Intake', icon: FileText },
  { key: 'adaptive', label: 'Expert Help', icon: MessageSquare },
  { key: 'template_complete', label: 'Review', icon: CheckCircle },
  { key: 'completed', label: 'Done', icon: Download },
];

export default function ChatPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [caseId, setCaseId] = useState(() => sessionStorage.getItem('active_case_id'));
  const [pendingProgress, setPendingProgress] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('pending_chat_progress') || '{}'); }
    catch { return {}; }
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [phase, setPhase] = useState('language_selection');
  const [showLangPicker, setShowLangPicker] = useState(false);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [showDocModal, setShowDocModal] = useState(false);
  const [isOffline, setIsOffline] = useState(false);
  const [retryQueue, setRetryQueue] = useState([]);
  const [showJourney, setShowJourney] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [consentPurpose, setConsentPurpose] = useState('');
  const [pendingUploadFile, setPendingUploadFile] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const chatContainerRef = useRef(null);
  const initialized = useRef(false);

  // Check online status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    setIsOffline(!navigator.onLine);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const sanitizeHtml = useCallback((text) => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }, []);

  const fetchDocument = useCallback(async () => {
    try {
      const { data } = await api.get(`/cases/${caseId}/document`);
      setDocumentPreview(data.document);
    } catch (err) {
      console.error('Failed to fetch document:', err);
    }
  }, [caseId]);

  const startChat = useCallback(async () => {
    try {
      setSending(true);
      const { data } = await api.post('/chat/start', { pre_fill: {} });
      setCaseId(null);
      sessionStorage.removeItem('active_case_id');
      setPendingProgress(data.pending_progress || {});
      sessionStorage.setItem('pending_chat_progress', JSON.stringify(data.pending_progress || {}));
      setPhase(data.phase);
      setMessages([{ 
        sender: 'assistant', 
        content: data.response, 
        phase: data.phase, 
        time: new Date() 
      }]);
    } catch (err) { 
      console.error('Failed to start chat:', err);
      setMessages([{ 
        sender: 'assistant', 
        content: 'Welcome to Instadrafts! I can help you with legal documents. What language would you prefer?', 
        phase: 'language_selection', 
        time: new Date() 
      }]);
    } finally {
      setSending(false);
    }
  }, []);

  const resumeChat = useCallback(async (cid) => {
    try {
      setSending(true);
      // 1. Get history
      const histRes = await api.get(`/chat/history/${cid}`);
      const history = histRes.data.messages.map(m => ({
        sender: ['ai', 'admin', 'lawyer', 'agent', 'system'].includes(m.sender_type.toLowerCase()) ? 'assistant' : 'user',
        content: m.content,
        phase: m.message_metadata?.phase,
        time: m.created_at
      }));
      setMessages(history);

      // 2. Get current case status
      const caseRes = await api.get(`/cases/${cid}`);
      const c = caseRes.data;
      setPhase(c.status === 'draft' ? 'template_complete' : c.runtime_source === 'adaptive-only' ? 'adaptive' : c.template_matched ? 'template_driven' : 'pre_intake');
      
      if (history.length === 0) {
        setMessages([{ 
          sender: 'assistant', 
          content: 'Welcome to Instadrafts! I can help you with legal documents. What language would you prefer?', 
          phase: 'language_selection', 
          time: new Date() 
        }]);
      }
    } catch (err) {
      console.error('Failed to resume chat:', err);
      sessionStorage.removeItem('active_case_id');
      sessionStorage.removeItem('pending_chat_progress');
      setCaseId(null);
    } finally {
      setSending(false);
    }
  }, [startChat]);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    if (caseId) {
      resumeChat(caseId);
    } else {
      startChat();
    }
  }, []);

  useEffect(() => { 
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); 
  }, [messages]);

  // Fetch document when phase is template_complete
  useEffect(() => {
    if (phase === 'template_complete' && caseId) {
      fetchDocument();
    }
  }, [phase, caseId]);

  const restartChat = async () => {
    if (!window.confirm('Are you sure? This will clear your current progress.')) return;
    try {
      setSending(true);
      const { data } = await api.post(`/chat/restart/${caseId}`);
      setPhase(data.phase);
      setDocumentPreview(null);
      setMessages([{ sender: 'assistant', content: data.response, phase: data.phase, time: new Date() }]);
    } catch (err) { 
      console.error('Failed to restart:', err);
      // Fallback: clear session and reload
      sessionStorage.removeItem('active_case_id');
      window.location.reload();
    } finally { 
      setSending(false); 
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !caseId || uploading) return;
    e.target.value = '';
    setPendingUploadFile(file);
    setConsentGiven(false);
    setConsentPurpose('');
    setShowConsentModal(true);
  };

  const handleConsentUpload = async () => {
    if (!consentGiven || !pendingUploadFile || !caseId) return;
    setShowConsentModal(false);
    const file = pendingUploadFile;
    setPendingUploadFile(null);
    setUploading(true);

    const userMsg = { sender: 'user', content: `📄 Uploading: ${file.name}`, time: new Date() };
    setMessages(prev => [...prev, userMsg]);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', '');
      formData.append('consent_given', 'true');
      formData.append('purpose_stated', consentPurpose || 'Legal document processing for case management');

      const { data } = await api.post(`/chat/${caseId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const responseText = data.efiling?.message || `✅ Uploaded: ${file.name}`;
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { sender: 'user', content: `📄 ${file.name}`, time: new Date() };
        return updated;
      });
      setMessages(prev => [...prev, {
        sender: 'assistant',
        content: responseText,
        phase: phase,
        time: new Date()
      }]);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Upload failed';
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { sender: 'user', content: `📄 ${file.name}`, time: new Date() };
        return updated;
      });
      setMessages(prev => [...prev, {
        sender: 'assistant',
        content: `❌ Upload failed: ${detail}`,
        error: true,
        time: new Date()
      }]);
    } finally {
      setUploading(false);
    }
  };

  const handleConsentCancel = () => {
    setShowConsentModal(false);
    setPendingUploadFile(null);
    setConsentGiven(false);
    setConsentPurpose('');
  };

  const sendMessage = async (text) => {
    if (!text?.trim() || sending) return;
    const msgText = text.trim();
    setInput('');
    setSending(true);

    const userMsg = { sender: 'user', content: msgText, time: new Date() };
    setMessages(prev => [...prev, userMsg]);

    // If offline, queue message
    if (isOffline) {
      setRetryQueue(prev => [...prev, msgText]);
      setMessages(prev => [...prev, { 
        sender: 'assistant', 
        content: 'You are offline. Message queued and will be sent when connection is restored.', 
        offline: true, 
        time: new Date() 
      }]);
      setSending(false);
      return;
    }

    try {
      const { data } = await api.post('/chat/message', {
        message: msgText,
        case_id: caseId,
        pre_fill: caseId ? undefined : pendingProgress,
      });
      if (data.field_updates || data.pending_progress) {
        const nextPending = data.pending_progress || data.field_updates || {};
        setPendingProgress(nextPending);
        if (!data.case_id) sessionStorage.setItem('pending_chat_progress', JSON.stringify(nextPending));
      }
      if (data.case_id) {
        setCaseId(data.case_id);
        sessionStorage.setItem('active_case_id', data.case_id);
        sessionStorage.removeItem('pending_chat_progress');
        setPendingProgress({});
      }
      setPhase(data.phase);
      
      setMessages(prev => [...prev, { 
        sender: 'assistant', 
        content: data.response, 
        phase: data.phase, 
        payment: data.payment,
        time: new Date() 
      }]);
    } catch (err) {
      setMessages(prev => [...prev, { 
        sender: 'assistant', 
        content: 'Something went wrong. Tap to retry.', 
        error: true, 
        retryMsg: msgText,
        time: new Date() 
      }]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleRetry = (msg) => {
    if (!msg.retryMsg) return;
    setMessages(prev => prev.filter(m => m !== msg));
    sendMessage(msg.retryMsg);
  };

  const handleLanguageSelect = (langCode) => {
    setShowLangPicker(false);
    sendMessage(langCode);
  };

  const downloadDocument = () => {
    if (!documentPreview?.content) return;
    const blob = new Blob([documentPreview.content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = documentPreview.filename || 'document.html';
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatTime = (d) => {
    if (!d) return '';
    const date = d instanceof Date ? d : new Date(d);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getCurrentStepIndex = () => {
    return JOURNEY_STEPS.findIndex(s => s.key === phase);
  };

  return (
    <div className="h-screen flex flex-col bg-[#0b141a]" data-testid="chat-page">
      {/* Offline Banner */}
      {isOffline && (
        <div className="bg-yellow-500 text-black px-4 py-2 text-xs font-medium flex items-center justify-center gap-2">
          <WifiOff className="w-4 h-4" /> You are offline. Messages will be queued.
        </div>
      )}

      {/* Header */}
      <header className="bg-[#202c33] px-4 py-2.5 flex items-center gap-3 shadow-sm z-10">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00a884] to-[#25d366] flex items-center justify-center">
          <Zap className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-white text-base font-semibold tracking-tight">Instadrafts</h1>
          <button 
            onClick={() => setShowJourney(!showJourney)}
            className="text-[#8696a0] text-xs flex items-center gap-1"
          >
            {JOURNEY_STEPS[getCurrentStepIndex()]?.label || 'online'} 
            <ChevronDown className={`w-3 h-3 transition-transform ${showJourney ? 'rotate-180' : ''}`} />
          </button>
        </div>
        <div className="flex items-center gap-2">
          {caseId && (
            <button onClick={restartChat} className="p-2 text-[#aebac1] hover:text-white transition" title="Start Again">
              <RotateCcw className="w-4 h-4" />
            </button>
          )}
          {user?.role === 'admin' && (
            <button onClick={() => navigate('/admin')} className="text-[#aebac1] hover:text-white px-3 py-1.5 text-xs font-mono uppercase border border-[#374045] rounded hover:border-[#00a884] transition">
              Admin
            </button>
          )}
          <button onClick={logout} className="p-2 text-[#aebac1] hover:text-white transition">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Journey Timeline - Collapsible */}
      {showJourney && (
        <div className="bg-[#202c33] border-b border-[#374045] px-4 py-3">
          <div className="flex items-center gap-2 overflow-x-auto">
            {JOURNEY_STEPS.map((step, idx) => {
              const isActive = phase === step.key;
              const isPast = getCurrentStepIndex() > idx;
              const StepIcon = step.icon;
              return (
                <div key={step.key} className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs whitespace-nowrap ${
                  isActive ? 'bg-[#00a884] text-white' : 
                  isPast ? 'bg-[#00a884]/30 text-[#00a884]' : 'text-[#8696a0]'
                }`}>
                  <StepIcon className="w-3 h-3" />
                  <span>{step.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Document Preview Banner */}
      {documentPreview && phase === 'template_complete' && (
        <div className="bg-[#00a884]/20 border-b border-[#00a884]/30 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#00a884]" />
              <div>
                <p className="text-white text-sm font-medium">Your document is ready!</p>
                <p className="text-[#8696a0] text-xs">{documentPreview.filename}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button 
                onClick={() => setShowDocModal(true)}
                className="px-3 py-1.5 bg-[#00a884] text-white text-xs rounded-lg hover:bg-[#06cf9c] transition"
              >
                Preview
              </button>
              <button 
                onClick={downloadDocument}
                className="px-3 py-1.5 bg-[#374045] text-white text-xs rounded-lg hover:bg-[#4a5560] transition flex items-center gap-1"
              >
                <Download className="w-3 h-3" /> Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23182229' fill-opacity='0.6'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        backgroundColor: '#0b141a'
      }}>
        <div className="max-w-3xl mx-auto px-4 py-4 space-y-1">
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] sm:max-w-[75%] rounded-lg px-3 py-2 relative ${
                msg.sender === 'user' 
                  ? 'bg-[#005c4b] text-white rounded-br-none' 
                  : 'bg-[#202c33] text-[#e9edef] rounded-bl-none'
              } ${msg.error ? 'border border-red-500/50 cursor-pointer' : ''} ${msg.offline ? 'border border-yellow-500/50' : ''}`}
                onClick={() => msg.error && handleRetry(msg)}
              >
                {msg.error && (
                  <div className="flex items-center gap-1 text-red-400 text-xs mb-1">
                    <AlertCircle className="w-3 h-3" /> Tap to retry
                  </div>
                )}
                {msg.offline && (
                  <div className="flex items-center gap-1 text-yellow-400 text-xs mb-1">
                    <WifiOff className="w-3 h-3" /> Offline
                  </div>
                )}
                <p className="text-sm whitespace-pre-wrap leading-relaxed" dangerouslySetInnerHTML={{ __html: sanitizeHtml(msg.content) }} />
                {msg.payment?.order_id && (
                  <div className="mt-3 rounded-md border border-[#00a884]/40 bg-[#00a884]/10 p-3">
                    <div className="flex items-center gap-2 text-[#9ff0d0] text-xs font-medium">
                      <CheckCircle className="w-4 h-4" /> Payment ready
                    </div>
                    <div className="mt-1 text-[11px] text-[#aebac1]">
                      Order {msg.payment.order_id} · Rs {msg.payment.amount}
                    </div>
                    {msg.payment.checkout_url && (
                      <a
                        href={msg.payment.checkout_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex mt-2 px-3 py-1.5 bg-[#00a884] text-white text-xs rounded hover:bg-[#06cf9c]"
                      >
                        Open payment link
                      </a>
                    )}
                  </div>
                )}
                <div className={`flex items-center gap-1 mt-1 ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <span className="text-[10px] opacity-60">{formatTime(msg.time)}</span>
                  {msg.sender === 'user' && (
                    <CheckCheck className="w-3 h-3 text-[#53bdeb]" />
                  )}
                </div>
              </div>
            </div>
          ))}

          {sending && (
            <div className="flex justify-start">
              <div className="bg-[#202c33] rounded-lg px-4 py-3">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-[#8696a0] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-[#8696a0] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-[#8696a0] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Language Picker Modal */}
      {showLangPicker && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-[#111b21] rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white text-lg font-semibold">Choose Language</h3>
              <button onClick={() => setShowLangPicker(false)} className="text-[#8696a0] hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {LANGUAGES.map(lang => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageSelect(lang.code)}
                  className="p-3 bg-[#202c33] rounded-xl text-left hover:bg-[#2a3942] transition"
                >
                  <span className="text-xl mr-2">{lang.flag}</span>
                  <span className="text-white text-sm">{lang.native}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Document Preview Modal */}
      {showDocModal && documentPreview && (
        <div className="fixed inset-0 bg-black/90 z-50 flex flex-col">
          <div className="bg-[#202c33] px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#00a884]" />
              <span className="text-white text-sm font-medium">Document Preview</span>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={downloadDocument}
                className="px-3 py-1.5 bg-[#00a884] text-white text-xs rounded-lg flex items-center gap-1"
              >
                <Download className="w-3 h-3" /> Download
              </button>
              <button onClick={() => setShowDocModal(false)} className="text-[#8696a0] hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto bg-white">
            <iframe 
              srcDoc={documentPreview.content} 
              className="w-full h-full"
              title="Document Preview"
            />
          </div>
        </div>
      )}

      {/* Consent Modal — DPDP Act s.6 */}
      {showConsentModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-[#111b21] rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white text-lg font-semibold">Document Consent</h3>
              <button onClick={handleConsentCancel} className="text-[#8696a0] hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-[#202c33] rounded-xl p-4">
                <p className="text-[#e9edef] text-sm leading-relaxed">
                  By uploading documents, you consent to Instadrafts collecting and processing your personal data for the purpose of legal document preparation and case management.
                </p>
                <ul className="mt-3 space-y-2 text-[#8696a0] text-xs">
                  <li className="flex items-start gap-2">
                    <Check className="w-3.5 h-3.5 mt-0.5 text-[#00a884] shrink-0" />
                    <span>Your documents are encrypted at rest (AES-256) and stored securely in India</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-3.5 h-3.5 mt-0.5 text-[#00a884] shrink-0" />
                    <span>Access is restricted to assigned lawyers, agents, and administrators only</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-3.5 h-3.5 mt-0.5 text-[#00a884] shrink-0" />
                    <span>Sensitive documents (Aadhaar, PAN) are masked and auto-deleted after retention period</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-3.5 h-3.5 mt-0.5 text-[#00a884] shrink-0" />
                    <span>You may request deletion of your documents at any time</span>
                  </li>
                </ul>
              </div>

              <div className="bg-[#202c33] rounded-xl p-4">
                <label className="text-[#8696a0] text-xs block mb-2">
                  Purpose of upload <span className="text-[#ea4335]">*</span>
                </label>
                <select
                  value={consentPurpose}
                  onChange={(e) => setConsentPurpose(e.target.value)}
                  className="w-full bg-[#2a3942] text-white text-sm rounded-lg px-3 py-2.5 border border-[#374045] focus:border-[#00a884] focus:outline-none"
                >
                  <option value="">Select purpose...</option>
                  <option value="Legal document processing for case management">Legal document processing</option>
                  <option value="Identity verification for case filing">Identity verification</option>
                  <option value="Portal form filling and e-filing">Portal e-filing</option>
                  <option value="Supporting document for legal opinion">Supporting document</option>
                </select>
              </div>

              <label className="flex items-start gap-3 bg-[#202c33] rounded-xl p-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consentGiven}
                  onChange={(e) => setConsentGiven(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-[#374045] bg-[#2a3942] text-[#00a884] focus:ring-[#00a884]"
                />
                <div>
                  <p className="text-white text-sm">
                    I consent to the collection and processing of my personal data <span className="text-[#ea4335]">*</span>
                  </p>
                  <p className="text-[#8696a0] text-xs mt-1">
                    Required under Digital Personal Data Protection Act, 2023 (DPDP)
                  </p>
                </div>
              </label>

              <div className="flex gap-3">
                <button
                  onClick={handleConsentCancel}
                  className="flex-1 px-4 py-2.5 bg-[#2a3942] text-[#8696a0] text-sm rounded-lg hover:bg-[#374045] transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConsentUpload}
                  disabled={!consentGiven || !consentPurpose}
                  className="flex-1 px-4 py-2.5 bg-[#00a884] text-white text-sm rounded-lg hover:bg-[#06cf9c] transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Upload Document
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="bg-[#202c33] px-3 py-2.5 border-t border-[#374045]">
        <div className="max-w-3xl mx-auto flex items-end gap-2">
          <button 
            onClick={() => setShowLangPicker(true)}
            className="p-2.5 text-[#aebac1] hover:text-white transition shrink-0"
            title="Change Language"
          >
            <Globe className="w-5 h-5" />
          </button>

          {caseId && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                className="hidden"
                onChange={handleFileSelect}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || sending}
                className="p-2.5 text-[#aebac1] hover:text-white transition shrink-0 disabled:opacity-40"
                title="Upload file"
              >
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
              </button>
            </>
          )}

          <div className="flex-1 bg-[#2a3942] rounded-lg flex items-end">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage(input);
                }
              }}
              placeholder={isOffline ? "Offline - message will be queued" : "Type a message..."}
              rows={1}
              className="w-full bg-transparent text-white text-sm px-3 py-2.5 resize-none focus:outline-none max-h-32"
              style={{ minHeight: '44px' }}
            />
          </div>

          <button
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || sending || isOffline}
            className="p-3 bg-[#00a884] text-white rounded-full hover:bg-[#06cf9c] transition disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
          >
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
